import React, { useState, useEffect } from "react";
import { Modal, Input, message } from "antd";
import { FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import dayjs from "dayjs";
import drEasyApi from "../../Api/Customapi";
import { useLanguage } from "../../context/LanguageContext";

const { TextArea } = Input;

const CancelBookingModal = ({ 
  visible, 
  onCancel, 
  booking, 
  onSuccess 
}) => {
  const { t } = useLanguage();
  const [cancelReason, setCancelReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset reason when modal opens/closes
  useEffect(() => {
    if (!visible) setCancelReason("");
  }, [visible]);

  const formatTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return "";
    const [hour, minute] = timeStr.split(":");
    const hourNum = parseInt(hour, 10);
    if (isNaN(hourNum)) return timeStr;
    const ampm = hourNum >= 12 ? t("booking.pm") : t("booking.am");
    const formattedHour = hourNum % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  const handleCancel = async () => {
    if (!booking) return;

    if (!cancelReason.trim()) {
      message.error(t("booking.provide_reason"));
      return;
    }

    setLoading(true);
    try {
      await drEasyApi.put("/api/Book/CancelBooking", {
        id: booking.id,
        reason: cancelReason.trim(),
      });

      message.success(t("booking.cancel_success"));

      if (onSuccess) onSuccess(booking.id);
      onCancel();
    } catch (error) {
      console.error("Failed to cancel booking:", error);
      message.error(
        error.response?.data[0] ||
          t("booking.cancel_failed")
      );
    }
    setLoading(false);
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <FaExclamationTriangle className="text-red-500" />
          <span>{t("booking.cancel_title")}</span>
        </div>
      }
      open={visible}
      onOk={handleCancel}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={t("booking.cancel_button")}
      cancelText={t("booking.keep_button")}
      okButtonProps={{ danger: true }}
      width={600}
      destroyOnClose
    >
      {booking && (
        <div className="space-y-7">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">{t("booking.details_title")}</h4>
            <div className="space-y-1 text-sm">
              <p>
                <strong>{t("booking.date")}:</strong>{" "}
                {dayjs(booking.bookingDate).format("DD MMM YYYY")}
              </p>
              <p>
                <strong>{t("booking.time")}:</strong>{" "}
                {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
              </p>
              <p>
                <strong>{t("booking.doctor")}:</strong> {booking.doctorName}
              </p>
              <p>
                <strong>{t("booking.clinic")}:</strong> {booking.clinicName}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {t("booking.reason_label")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <TextArea
              rows={4}
              placeholder={t("booking.reason_placeholder")}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              maxLength={500}
              showCount
            />
          </div>

          <div className="text-sm text-gray-600">
            <p className="flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 text-blue-500 flex-shrink-0" />
              {t("booking.cannot_restore")}
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CancelBookingModal;
